import { useCallback, useEffect, useState } from "react";
import { Download, Upload, DatabaseBackup, RotateCcw, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import * as api from "@/lib/api";

const KIND = { notturno: "Notturno", manuale: "Manuale", importato: "Importato da file" };
const fmt = (d) => new Date(d).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
const err = (e, m) => toast.error(e?.response?.data?.detail || m);

function RestorePanel({ backup, onDone }) {
  const [pats, setPats] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.backupPatients(backup.id).then(setPats).catch((e) => { err(e, "Errore lettura backup"); setPats([]); }); }, [backup.id]);
  const restore = async () => {
    setBusy(true);
    try {
      const r = await api.restoreFromBackup(backup.id, sel);
      toast.success(r.restored.length ? `Ripristinati: ${r.restored.join(", ")}` : "Nessun paziente da ripristinare");
      onDone();
    } catch (e) { err(e, "Ripristino non riuscito"); } finally { setBusy(false); }
  };
  if (!pats) return <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  return (
    <div className="space-y-1 border-t border-white/5 p-3" data-testid={`backup-restore-${backup.id}`}>
      {pats.map((p) => (
        <label key={p.id} className={`flex items-center gap-2 rounded px-2 py-1.5 text-xs ${p.exists ? "opacity-50" : "cursor-pointer hover:bg-white/5"}`}>
          <input type="checkbox" disabled={p.exists} checked={sel.includes(p.id)} onChange={(e) => setSel((s) => (e.target.checked ? [...s, p.id] : s.filter((x) => x !== p.id)))} data-testid={`backup-patient-${p.id}`} />
          <span className="flex-1 truncate">{p.name}</span>
          <span className="text-[10px] italic">{p.exists ? "già presente" : "mancante"}</span>
        </label>
      ))}
      {!pats.length && <div className="py-2 text-center text-xs text-muted-foreground">Nessun paziente in questo backup.</div>}
      <button onClick={restore} disabled={busy || !sel.length} className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-bold text-primary-foreground disabled:opacity-40" data-testid={`backup-restore-confirm-${backup.id}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Ripristina {sel.length || ""} {sel.length === 1 ? "paziente" : "pazienti"}
      </button>
    </div>
  );
}

function BackupItem({ b, open, toggle, onDone }) {
  const [dl, setDl] = useState(false);
  const download = async () => {
    setDl(true);
    try {
      const r = await api.downloadBackup(b.id);
      const name = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "agfit-backup.json.gz";
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = u; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(u), 30000);
    } catch (e) { toast.error("Download non riuscito"); } finally { setDl(false); }
  };
  return (
    <div className="rounded-lg border border-white/10 bg-black/20" data-testid={`backup-item-${b.id}`}>
      <div className="flex items-center gap-2 px-3 py-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">{fmt(b.created_at)}</div>
          <div className="text-[11px] text-muted-foreground">{KIND[b.kind] || b.kind} · {b.patients} {b.patients === 1 ? "paziente" : "pazienti"} · {(b.size / 1024 / 1024).toFixed(1)} MB</div>
        </div>
        <button onClick={download} title="Scarica" className="rounded-md border border-white/10 p-1.5 hover:bg-white/5" data-testid={`backup-download-${b.id}`}>{dl ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}</button>
        <button onClick={toggle} title="Ripristina pazienti" className={`rounded-md border p-1.5 ${open ? "border-primary text-primary" : "border-white/10 hover:bg-white/5"}`} data-testid={`backup-open-restore-${b.id}`}><RotateCcw className="h-4 w-4" /></button>
      </div>
      {open && <RestorePanel backup={b} onDone={onDone} />}
    </div>
  );
}

export default function BackupPanel({ onRestored }) {
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.listBackups().then(setItems).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);
  const make = async () => { setBusy("make"); try { await api.createBackup(); toast.success("Backup creato"); load(); } catch (e) { err(e, "Backup non riuscito"); } finally { setBusy(""); } };
  const upload = async (f) => {
    if (!f) return;
    setBusy("up");
    try { const b = await api.uploadBackup(f); toast.success("Backup caricato: scegli i pazienti da ripristinare"); await load(); setOpenId(b.id); } catch (e) { err(e, "File non valido"); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-3" data-testid="backup-panel">
      <p className="text-xs text-muted-foreground">Ogni notte alle 3:00 viene salvata una copia di tutti i dati (conservata 30 giorni). Il ripristino aggiunge solo i pazienti mancanti, senza toccare quelli esistenti.</p>
      <div className="grid grid-cols-2 gap-2">
        <button onClick={make} disabled={!!busy} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-xs font-semibold hover:bg-white/5 disabled:opacity-50" data-testid="backup-create">
          {busy === "make" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <DatabaseBackup className="h-3.5 w-3.5" />} Backup adesso
        </button>
        <label className={`inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-xs font-semibold hover:bg-white/5 ${busy ? "pointer-events-none opacity-50" : ""}`}>
          {busy === "up" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Carica file backup
          <input type="file" accept=".gz,.json,application/gzip,application/json" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="backup-upload-input" />
        </label>
      </div>
      {items === null ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
        : !items.length ? <div className="py-6 text-center text-sm text-muted-foreground" data-testid="backup-empty">Nessun backup ancora. Il primo verrà creato stanotte.</div>
        : <div className="space-y-1.5" data-testid="backup-list">{items.map((b) => <BackupItem key={b.id} b={b} open={openId === b.id} toggle={() => setOpenId(openId === b.id ? null : b.id)} onDone={() => { setOpenId(null); onRestored?.(); }} />)}</div>}
      {items?.length > 0 && <div className="flex items-center gap-1 text-[10px] text-muted-foreground"><Check className="h-3 w-3" /> Il file scaricato contiene solo i pazienti dell'area in cui ti trovi.</div>}
    </div>
  );
}
